/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Chart scroll regions must be focusable for keyboard scrolling. */
import { type FC, useId, useLayoutEffect, useMemo, useRef } from "react";

import { Box, Flex, Skeleton, Text } from "@mantine/core";
import { useElementSize, useMergedRef } from "@mantine/hooks";
import { Colors, Tooltip as SdsTooltip, Spacing } from "@snapchat/snap-design-system";
import { InformationIcon } from "@snapchat/snap-design-system-icons";
import { Bar, CartesianGrid, Cell, BarChart as RechartsBarChart, Tooltip, XAxis, YAxis } from "recharts";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { type PayoutPeriod, PayoutTimeGranularity } from "../../../../gql-generated/types";
import { formatMilliUsd } from "../../../../lens-creator-payout/utils/formatMilliUsd";
import { usePayoutStatsHistory } from "../../../hooks";
import {
    MonthlyPayouts,
    MonthlyPayoutsTooltip as MonthlyPayoutsTooltipMessage,
    NoDataAvailable,
    ScrollForMoreMonths,
} from "../messages.generated";

import { chart, chartArea, container, scrollHint } from "./MonthlyPayoutsChart.module.css";
import { MonthlyPayoutsTooltip } from "./MonthlyPayoutsTooltip";
import { CHART_HEIGHT } from "./constants";

export interface MonthlyPayoutDataPoint {
    time: number;
    month: string;
    label: string;
    amount: number;
    amountMilliUsd: number;
}

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const Y_AXIS_WIDTH = 80;
const MIN_MONTH_WIDTH = 100;

// limit:0 hits a backend default page size that returns a single period for
// some windows; 60 monthly buckets (5 years) covers any selectable range
// including All Time / Lifetime. usePayoutStatsHistory exposes hasMore for
// a future paginate-on-demand caller if a creator's lifetime exceeds this.
const MAX_MONTHLY_BUCKETS = 60;

type MonthlyPayoutPeriod = Pick<PayoutPeriod, "period" | "totalAmountMilliUsd">;

const formatPeriodToChartData = (periods: readonly MonthlyPayoutPeriod[]): MonthlyPayoutDataPoint[] =>
    periods
        .filter((p): p is MonthlyPayoutPeriod & { period: string } => p.period !== null && p.period !== undefined)
        .map((period) => {
            const date = new Date(period.period);
            const monthIndex = date.getUTCMonth();
            const year = date.getUTCFullYear();
            const shortMonth = (MONTH_NAMES[monthIndex] ?? "").slice(0, 3);

            return {
                time: date.getTime(),
                month: `${shortMonth} ${year}`,
                label: `${MONTH_NAMES[monthIndex]} ${year}`,
                amount: period.totalAmountMilliUsd / 1000,
                amountMilliUsd: period.totalAmountMilliUsd,
            };
        })
        .sort((a, b) => a.time - b.time);

interface MonthlyPayoutsChartProps {
    periods?: readonly MonthlyPayoutPeriod[];
    onMonthSelect?: (month: string) => void;
    showHeading?: boolean;
    scrollable?: boolean;
    selectedMonth?: string;
    startDate?: string;
    endDate?: string;
    unlockableId?: string;
    skip?: boolean;
    isResolvingSelectedLens?: boolean;
}

export const MonthlyPayoutsChart: FC<MonthlyPayoutsChartProps> = ({
    periods: suppliedPeriods,
    onMonthSelect,
    showHeading = true,
    scrollable = false,
    selectedMonth,
    startDate,
    endDate,
    unlockableId,
    skip,
    isResolvingSelectedLens = false,
}) => {
    const labelId = useId();
    const hintId = `${labelId}-scroll-hint`;
    const { ref, width: chartWidth } = useElementSize();
    const viewportRef = useRef<HTMLDivElement>(null);
    const chartRef = useMergedRef(ref, viewportRef);
    const lastScrolledMonth = useRef<string>();
    const { loading: configLoading } = useFeatureGates();
    const { periods, loading } = usePayoutStatsHistory({
        startDate,
        endDate,
        countryFilters: [],
        granularity: PayoutTimeGranularity.TimeGranularityMonthly,
        limit: MAX_MONTHLY_BUCKETS,
        unlockableId,
        skip: suppliedPeriods !== undefined || skip,
    });

    const data = useMemo(() => formatPeriodToChartData(suppliedPeriods ?? periods), [suppliedPeriods, periods]);
    const isLoading = loading || configLoading || chartWidth === 0 || isResolvingSelectedLens;
    const renderedWidth = scrollable ? Math.max(chartWidth, data.length * MIN_MONTH_WIDTH + Y_AXIS_WIDTH) : chartWidth;
    const hasOverflow = !isLoading && data.length > 0 && renderedWidth > chartWidth;

    useLayoutEffect(() => {
        if (!scrollable || !selectedMonth) {
            lastScrolledMonth.current = undefined;
            return;
        }
        if (isLoading || !viewportRef.current || lastScrolledMonth.current === selectedMonth) return;
        const selectedTime = new Date(`${selectedMonth}-01T00:00:00Z`).getTime();
        if (!Number.isFinite(selectedTime) || data.length === 0) return;
        // Pending months have no paid bar; show their latest available payout.
        const nextIndex = data.findIndex((point) => point.time > selectedTime);
        const index = nextIndex === -1 ? data.length - 1 : Math.max(0, nextIndex - 1);
        const monthCenter = Y_AXIS_WIDTH + ((index + 0.5) * (renderedWidth - Y_AXIS_WIDTH)) / data.length;
        viewportRef.current.scrollLeft = Math.max(
            0,
            Math.min(monthCenter - chartWidth / 2, renderedWidth - chartWidth),
        );
        // Refreshes and resizes must preserve any subsequent manual scroll.
        lastScrolledMonth.current = selectedMonth;
    }, [chartWidth, data, isLoading, renderedWidth, scrollable, selectedMonth]);

    return (
        <Box w="100%" className={container}>
            {showHeading && (
                <Flex gap={Spacing["1XS"]} align="center" mb="md">
                    <span className="subheading-sh400">
                        <MonthlyPayouts />
                    </span>
                    <SdsTooltip content={<MonthlyPayoutsTooltipMessage />}>
                        <InformationIcon />
                    </SdsTooltip>
                </Flex>
            )}
            <div
                className={chartArea}
                ref={chartRef}
                role={scrollable ? "region" : undefined}
                aria-labelledby={scrollable ? labelId : undefined}
                aria-describedby={hasOverflow ? hintId : undefined}
                tabIndex={scrollable ? 0 : undefined}
            >
                {scrollable && (
                    <span id={labelId} hidden>
                        <MonthlyPayouts />
                    </span>
                )}
                {isLoading ? (
                    <Skeleton h={CHART_HEIGHT} w="100%" />
                ) : data.length === 0 ? (
                    <Flex h={CHART_HEIGHT} align="center" justify="center">
                        <Text className="paragraph-p200" c="dimmed">
                            <NoDataAvailable />
                        </Text>
                    </Flex>
                ) : (
                    <div className={chart}>
                        <RechartsBarChart width={renderedWidth} height={CHART_HEIGHT} data={data}>
                            <CartesianGrid horizontal strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="month"
                                tickLine={false}
                                axisLine={false}
                                tick={{ transform: `translate(0, ${Spacing["1XS"]})` }}
                                interval={scrollable ? 0 : "preserveStartEnd"}
                                minTickGap={Spacing["2XS"]}
                            />
                            <YAxis
                                width={Y_AXIS_WIDTH}
                                tickLine={false}
                                axisLine={false}
                                tick={{ transform: `translate(-${Spacing.SM}, 0)` }}
                                tickFormatter={(value: number) => formatMilliUsd(value * 1000)}
                            />
                            <Tooltip content={MonthlyPayoutsTooltip} cursor={false} />
                            <Bar
                                dataKey="amount"
                                isAnimationActive={false}
                                onClick={
                                    onMonthSelect
                                        ? (entry) => {
                                              const point = data[entry.originalDataIndex];
                                              if (point) onMonthSelect(new Date(point.time).toISOString().slice(0, 7));
                                          }
                                        : undefined
                                }
                                fill={Colors.Black.V50}
                                activeBar={{ fill: Colors.Yellow.V200 }}
                                radius={[Spacing["2XS"], Spacing["2XS"], 0, 0]}
                            >
                                {onMonthSelect &&
                                    data.map((point) => (
                                        <Cell
                                            key={point.time}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={point.label}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onMonthSelect(new Date(point.time).toISOString().slice(0, 7));
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    event.currentTarget.dispatchEvent(
                                                        new MouseEvent("click", { bubbles: true }),
                                                    );
                                                }
                                            }}
                                        />
                                    ))}
                            </Bar>
                        </RechartsBarChart>
                    </div>
                )}
            </div>
            {hasOverflow && (
                <Text id={hintId} className={`paragraph-p300 ${scrollHint}`}>
                    <ScrollForMoreMonths />
                </Text>
            )}
        </Box>
    );
};

/* eslint-enable jsx-a11y/no-noninteractive-tabindex -- End of keyboard-scroll viewport component. */
